import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const bloodDrop = require('../../assets/imgs/blood_drop_black.png');

/**
 * Symmetric reference-range bar for the MVX "optimal-in-the-middle" factors
 * (citrate + the three BCAAs). Mirrors the app's standard ReferenceRangeChart
 * (default layout) exactly: green optimal centered on a normalized −133.3…+133.3
 * axis, with Lab (yellow) / > Lab (orange) / Alarm (red) shoulders mirrored on
 * BOTH sides, band names centered above, boundary values labeled below, and a
 * blood-drop marker mapped piecewise into its zone.
 */
const GREEN = '#78F880';
const YELLOW = '#FEFA83';
const ORANGE = '#FFD37E';
const RED = '#FC6552';
const GRID_LINE_COLOR = '#CDD7EA';
const AXIS_MIN = -133.3;
const AXIS_MAX = 133.3;

export type MvxOptimalBarProps = {
  optimal: [number, number];
  reference: [number, number];
  severe: [number, number];
  value: number;
  format: (n: number) => string;
  /** Show the band-name labels above the bar (usually only the first such row). */
  showLabels?: boolean;
};

const reflowSoon = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    try {
      chart.reflow();
    } catch (e) {
      /* chart unmounted */
    }
  }, 0);
};

export class MvxOptimalBar extends React.Component<MvxOptimalBarProps> {
  // Anchor value-space boundaries to the normalized axis, mirrored around the
  // optimal midpoint (0). severe→±100, reference→±66.6, optimal→±33.3.
  private anchors(): Array<[number, number]> {
    const [optLo, optHi] = this.props.optimal;
    const [refLo, refHi] = this.props.reference;
    const [sevLo, sevHi] = this.props.severe;
    const mean = (optLo + optHi) / 2;
    return [
      [sevLo, -100],
      [refLo, -66.6],
      [optLo, -33.3],
      [mean, 0],
      [optHi, 33.3],
      [refHi, 66.6],
      [sevHi, 100],
    ];
  }

  // value → normalized position, piecewise-linear across the anchors, clamped
  // into the outer (red) bands so an off-range value stays on-scale.
  private toNorm(value: number): number {
    const a = this.anchors();
    if (value <= a[0][0]) {
      const [v0, n0] = a[0];
      const [v1, n1] = a[1];
      return Math.max(AXIS_MIN, n0 + ((value - v0) * (n1 - n0)) / (v1 - v0));
    }
    const last = a.length - 1;
    if (value >= a[last][0]) {
      const [v0, n0] = a[last - 1];
      const [v1, n1] = a[last];
      return Math.min(AXIS_MAX, n1 + ((value - v1) * (n1 - n0)) / (v1 - v0));
    }
    for (let i = 0; i < last; i++) {
      const [v0, n0] = a[i];
      const [v1, n1] = a[i + 1];
      if (value <= v1) return n0 + ((value - v0) * (n1 - n0)) / (v1 - v0);
    }
    return 0;
  }

  render() {
    const { optimal, reference, severe, value, format, showLabels } = this.props;
    const [optLo, optHi] = optimal;
    const [refLo, refHi] = reference;
    const [sevLo, sevHi] = severe;
    const normValue = this.toNorm(value);
    const offset = normValue >= 0 ? -12 : 12;

    // Boundary numbers below the bar (mean and the chart ends stay blank).
    const numByPos: { [k: string]: number } = {
      '-100': sevLo,
      '-66.6': refLo,
      '-33.3': optLo,
      '33.3': optHi,
      '66.6': refHi,
      '100': sevHi,
    };

    const band = (from: number, to: number, color: string, text: string): Highcharts.YAxisPlotBandsOptions => ({
      from,
      to,
      color,
      label: showLabels
        ? { text, align: 'center', verticalAlign: 'top', y: -5, style: { color: '#333', fontSize: '10px', fontWeight: 'bold' } }
        : undefined,
    });

    const options: Highcharts.Options = {
      chart: { type: 'bar', height: 60, marginTop: 20, marginBottom: 20, backgroundColor: 'transparent' },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0 }],
      yAxis: {
        title: { text: '' },
        min: AXIS_MIN,
        max: AXIS_MAX,
        tickPositions: [-133.3, -100, -66.6, -33.3, 0, 33.3, 66.6, 100, 133.3],
        labels: {
          enabled: true,
          formatter: function () {
            const key = String((this as any).value);
            return key in numByPos ? format(numByPos[key]) : '';
          },
          style: { fontSize: '10px', color: '#555' },
        },
        gridLineWidth: 1,
        gridLineColor: GRID_LINE_COLOR,
        plotBands: [
          band(-133.3, -100, RED, 'Alarm'),
          band(-100, -66.6, ORANGE, '< Lab'),
          band(-66.6, -33.3, YELLOW, 'Lab'),
          band(-33.3, 33.3, GREEN, 'Optimal'),
          band(33.3, 66.6, YELLOW, 'Lab'),
          band(66.6, 100, ORANGE, '> Lab'),
          band(100, 133.3, RED, 'Alarm'),
        ],
      },
      tooltip: { enabled: false },
      series: [
        {
          type: 'bar',
          color: 'rgba(255,255,255,0)',
          borderWidth: 0,
          pointWidth: 10,
          data: [normValue],
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            useHTML: true,
            verticalAlign: 'middle',
            x: offset,
            format: `<img src="${bloodDrop}" />`,
          },
        } as Highcharts.SeriesBarOptions,
      ],
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    return (
      <HighchartsReact
        options={options}
        highcharts={Highcharts}
        callback={reflowSoon}
        containerProps={{ style: { width: '100%', overflow: 'hidden' } }}
      />
    );
  }
}

export default MvxOptimalBar;
